import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Package, ArrowLeft } from "lucide-react";
import { useCart } from "../context/CartContext";

export default function Success() {
  const API_BASE = import.meta.env.VITE_API_BASE_URL;
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { clearCart } = useCart();

  // Clear cart only after order successfully loads
  useEffect(() => {
    if (order) {
      clearCart();
    }
  }, [order, clearCart]);

  // Retry loop until webhook finishes updating DB
    useEffect(() => {
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 15; // increased
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/orders/by-session?session_id=${sessionId}&t=${Date.now()}`
        );

        if (res.data) {
          setOrder(res.data);
          clearInterval(interval);
          setLoading(false);
        }
      } catch (err) {
        attempts++;
        console.log(`Retry #${attempts}`);

        if (attempts >= maxAttempts) {
          clearInterval(interval);
          setLoading(false);
        }
      }
    }, 1500); // 1.5 sec between attempts

    return () => clearInterval(interval);
  }, [sessionId, API_BASE]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 text-lg">
        Processing your payment… Please wait ⏳
      </div>
    );

  if (!order)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-3xl font-bold mb-2">Order Not Found</h1>
        <p className="text-gray-500 mb-6">
          We couldn't retrieve your order yet. Please check your email for confirmation.
        </p>
        <Link to="/" className="px-5 py-3 rounded-lg bg-black text-white">
          Back to Home
        </Link>
      </div>
    );

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 animate-fadeIn">
      <div className="flex items-center justify-center mb-6">
        <CheckCircle size={60} className="text-green-600 animate-scaleIn" />
      </div>

      <h1 className="text-4xl font-bold text-center mb-2">Thank You! 🎉</h1>
      <p className="text-center text-gray-600 mb-8">
        Your order was placed successfully. We’ve sent your confirmation email!
      </p>

      <div className="bg-white shadow-xl rounded-2xl p-6 border border-gray-100 mb-8">
        <h2 className="text-xl font-semibold mb-5 flex items-center gap-2">
          <Package size={22} /> Order Summary
        </h2>

        <div className="space-y-2 text-sm">
  <p><strong>Order ID:</strong> {order._id}</p>
  <p><strong>Total Paid:</strong> ${(order.amount / 100).toFixed(2)}</p>
  <p><strong>Email:</strong> {order.customerEmail}</p>
  
  {/* Shipping */}
  {order.shippingInfo && (
    <div className="mt-4">
      <h4 className="font-semibold mb-1">Shipping To:</h4>
      <p>
        {order.shippingInfo.name} <br />
        {order.shippingInfo.address} <br />
        {order.shippingInfo.city}, {order.shippingInfo.state}{" "}
        {order.shippingInfo.zip} <br />
        {order.shippingInfo.country}
      </p>
    </div>
  )}
</div>

        <div className="mt-6">
          <h3 className="font-semibold mb-3">Items</h3>
          <div className="space-y-3">
            {order.items.map((i, idx) => (
              <div
                key={idx}
                className="border rounded-lg p-4 bg-gray-50 flex justify-between items-center"
              >
                <div>
                  <div className="font-medium">
                    {i.name}
                    {i.variantName && (
                      <span className="text-gray-500"> ({i.variantName})</span>
                    )}
                  </div>
                  <div className="text-gray-600 text-sm">
                    ${i.price.toFixed(2)}
                  </div>
                </div>

                <div className="text-gray-700 font-semibold">× {i.qty}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA buttons */}
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Link
          to="/my-orders"
          className="bg-black text-white px-6 py-3 rounded-lg text-center shadow hover:bg-gray-900 transition"
        >
          Track My Order
        </Link>
       
        <Link
          to="/"
          className="bg-gray-200 px-6 py-3 rounded-lg text-center flex items-center justify-center gap-2 hover:bg-gray-300 transition"
        >
          <ArrowLeft size={18} /> Back to Home
        </Link>
        <Link to="/my-orders" className="text-blue-600 underline mt-4">
  View your orders manually →
</Link>
      </div>
    </div>
  );
}
